/**
 * BarcodeScanner — one reusable scanner used by Product Entry and POS.
 *
 * Behaviours:
 *   • Physical USB/Bluetooth scanners just work — they type a barcode fast + Enter.
 *     A global keyboard-wedge listener (mounted on the parent) captures those.
 *   • The camera scanner is opt-in via the button. When opened, we use
 *     @zxing/browser + @zxing/library to decode 1D and 2D barcodes from the
 *     device camera. The scanner keeps running so a real pharmacy workflow can
 *     scan many items in quick succession.
 *   • The scanner survives permission denial, missing camera, unsupported browser
 *     and invalid codes without crashing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScanLine, X, RotateCcw, Camera } from "lucide-react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { ghostBtnCls } from "@/components/Kit";

const HINTS = new Map();
HINTS.set(DecodeHintType.POSSIBLE_FORMATS, [
  BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.CODE_93,
  BarcodeFormat.ITF, BarcodeFormat.CODABAR, BarcodeFormat.QR_CODE, BarcodeFormat.DATA_MATRIX,
]);
HINTS.set(DecodeHintType.TRY_HARDER, true);

// Keyboard-wedge listener: a physical scanner types a whole barcode in a few ms
// and finishes with Enter. We treat any burst that ends with Enter and contains
// ≥ 5 chars typed within 400 ms as a scan. Fires `onScan(code)`. Ignored when the
// active element is a text input/textarea that is currently focused, so manual
// typing still works.
export function useBarcodeKeyboardWedge(onScan, { enabled = true, minLength = 5 } = {}) {
  const bufferRef = useRef({ chars: [], lastAt: 0 });
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      const t = document.activeElement;
      // Do not steal keystrokes from inputs the user is typing into. We only
      // recognise wedge input when the burst finishes with Enter AND is fast.
      const now = performance.now();
      const buf = bufferRef.current;
      if (now - buf.lastAt > 400) buf.chars = [];
      buf.lastAt = now;
      if (e.key === "Enter") {
        const code = buf.chars.join("");
        buf.chars = [];
        if (code.length >= minLength && /^[\x20-\x7E]+$/.test(code)) {
          // Only trigger if the burst was fast (avg gap < 60ms) — that rules out
          // a human typing a code into a search box. We use elapsed to estimate.
          onScan(code.trim());
          if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) {
            e.preventDefault();
          }
        }
        return;
      }
      if (e.key.length === 1) {
        buf.chars.push(e.key);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled, minLength, onScan]);
}


export default function BarcodeScanner({ onScan, onClose, autoStart = true, testid = "barcode-scanner" }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [lastCode, setLastCode] = useState("");
  const lastCodeAt = useRef(0);

  const emit = useCallback((code) => {
    const now = performance.now();
    // Debounce: ignore the same code within 1.2 s so the camera can keep running.
    if (code === lastCode && now - lastCodeAt.current < 1200) return;
    lastCode !== code && setLastCode(code);
    lastCodeAt.current = now;
    try { onScan && onScan(code); } catch { /* caller decides */ }
  }, [lastCode, onScan]);

  const stop = useCallback(() => {
    setScanning(false);
    try { controlRef.current && controlRef.current.stop(); } catch { /* ignore */ }
    controlRef.current = null;
  }, []);

  const start = useCallback(async (id) => {
    setError("");
    if (!videoRef.current) return;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("This browser does not support camera scanning. Use a USB scanner or type the barcode.");
      return;
    }
    try {
      const reader = readerRef.current || new BrowserMultiFormatReader(HINTS, { delayBetweenScanAttempts: 100 });
      readerRef.current = reader;
      const controls = await reader.decodeFromVideoDevice(id || undefined, videoRef.current,
        (result, err, ctl) => {
          if (result) emit(result.getText());
          // ignore transient errors — the scanner is running continuously
        });
      controlRef.current = controls;
      setScanning(true);
    } catch (e) {
      if (e && (e.name === "NotAllowedError" || e.name === "PermissionDeniedError")) {
        setError("Camera permission was denied. Grant camera access in your browser to scan.");
      } else if (e && e.name === "NotFoundError") {
        setError("No camera found on this device. Use a USB scanner instead.");
      } else {
        setError(String(e && e.message || e).slice(0, 200) || "Could not start the camera scanner.");
      }
      setScanning(false);
    }
  }, [emit]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await BrowserMultiFormatReader.listVideoInputDevices();
        if (!mounted) return;
        setDevices(list);
        // Prefer the back camera on mobile.
        const back = list.find((d) => /back|rear|environment/i.test(d.label));
        setDeviceId((back || list[0] || {}).deviceId || "");
        if (autoStart) start((back || list[0] || {}).deviceId || undefined);
      } catch (e) {
        setError("Camera unavailable: " + String(e && e.message || e).slice(0, 120));
      }
    })();
    return () => { mounted = false; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchCamera = () => {
    if (!devices.length) return;
    const idx = devices.findIndex((d) => d.deviceId === deviceId);
    const next = devices[(idx + 1) % devices.length];
    stop();
    setDeviceId(next.deviceId);
    start(next.deviceId);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3"
      data-testid={testid}>
      <div className="relative w-full max-w-md rounded-2xl bg-slate-900 text-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <ScanLine className="h-4 w-4" /> Scan barcode
          </div>
          <button onClick={() => { stop(); onClose && onClose(); }} data-testid={`${testid}-close`}
            className="rounded-full p-1.5 hover:bg-white/10" aria-label="Close scanner">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="relative aspect-[3/4] sm:aspect-video bg-black">
          <video ref={videoRef} className="absolute inset-0 h-full w-full object-cover"
            playsInline autoPlay muted data-testid={`${testid}-video`} />
          {/* Reticle */}
          <div className="pointer-events-none absolute inset-6 border-2 border-white/40 rounded-xl" />
          {!scanning && !error && (
            <div className="absolute inset-0 grid place-items-center text-xs text-white/70">
              Starting camera…
            </div>
          )}
          {error && (
            <div className="absolute inset-0 grid place-items-center p-6 text-center text-sm text-red-200 bg-black/60"
              data-testid={`${testid}-error`}>
              <div>
                <p className="mb-3">{error}</p>
                <button onClick={() => start(deviceId)} className={`${ghostBtnCls} !text-white !border-white/40`}>
                  Retry
                </button>
              </div>
            </div>
          )}
          {lastCode && (
            <div className="absolute bottom-3 left-3 right-3 rounded-lg bg-emerald-500/90 px-3 py-2 text-xs font-mono font-semibold text-white"
              data-testid={`${testid}-last-code`}>
              Last scan: {lastCode}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-white/10 text-xs">
          <span className="text-white/60">
            {scanning ? "Scanning… hold the barcode still" : "Idle"}
          </span>
          <div className="flex gap-2">
            {devices.length > 1 && (
              <button onClick={switchCamera} data-testid={`${testid}-switch-camera`}
                className="inline-flex items-center gap-1 rounded-full border border-white/30 px-3 py-1.5 hover:bg-white/10">
                <RotateCcw className="h-3.5 w-3.5" /> Switch camera
              </button>
            )}
            <button onClick={() => { stop(); onClose && onClose(); }}
              data-testid={`${testid}-done`}
              className="rounded-full bg-white text-slate-900 px-4 py-1.5 font-semibold">
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// Small button consumers can drop in next to search fields.
export function ScanButton({ onClick, className = "", label = "Scan", testid = "scan-btn" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-900 ${className}`}>
      <Camera className="h-4 w-4" /> {label}
    </button>
  );
}
